import { DefaultValues, useForm, UseFormReturn } from 'react-hook-form'
import { useCallback, useContext } from 'react'
import { UserContext } from 'shared/users/contexts/UserContext'

import { DeclarationTypeEnum, SecurityEnum } from 'features/declaration/models/schemas/declarationEnums'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { sortBySequenceNumber } from 'shared/lib/common-util'
import {
  blankCarrier, blankConsignee, blankConsignor,
  blankDeclarant, blankGuarantee, blankLocationOfGoods,
  blankPlaceOfAcceptance,
  blankPlaceOfDelivery,
  blankPlaceOfLoading,
  blankPlaceOfUnloading,
} from 'features/declaration/models/blankItems'
import {
  CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum,
} from '../../../../api/models'
import transitOperationFormResolver from './transitOperationFormResolver'
import { Declaration } from '../../models/types'
import { useDeclarationEditorContext, useServerRequirementsContext } from '../../form/contexts/DeclarationEditorContext'
import TransitOperationFormResolverContext from './TransitOperationFormResolverContext'
import { toPreviousDocuments, toSupportingDocuments, toTransportDocuments } from '../../document/models/modelToModel'

export const initialFormData: DefaultValues<TransitOperationFormValues> = {
  id: null,
  deleted: false,
  status: 'DRAFT',
  mrn: null,
  ensMrn: null,
  security: SecurityEnum.Enum['0'],
  limitDate: '',
  declarationType: DeclarationTypeEnum.Enum.T1,
  specificCircumstanceIndicator: '',
  additionalDeclarationType: CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum.A,
  lrn: null,
  countryOfDispatch: '',
  countryOfDestination: '',
  containerIndicator: 'false',
  inlandModeOfTransport: '',
  borderModeOfTransport: '',
  bindingItinerary: 'false',
  entryIndicator: 'false',
  grossMass: 0,
  customerId: 0,
  referenceNumberUCR: null,
  transportChargesMethodOfPayment: null,
  departureCustomsOffice: '',
  destinationCustomsOffice: null,
  consignmentId: null,
  carrier: blankCarrier,
  consignor: blankConsignor,
  consignee: blankConsignee,
  declarant: blankDeclarant,
  locationOfGoods: blankLocationOfGoods,
  placeOfLoading: blankPlaceOfLoading,
  placeOfUnloading: blankPlaceOfUnloading,
  placeOfAcceptance: blankPlaceOfAcceptance,
  placeOfDelivery: blankPlaceOfDelivery,
  transitCustomsOffices: [],
  customsOfficesOfTransitExit: [],
  guarantee: blankGuarantee,
  authorisations: [],
  additionalSupplyChainActor: [],
  transportEquipment: [],
  departureTransportMeans: [],
  countryOfRoutingOfConsignment: [],
  activeBorderTransportMeans: [],
  previousDocument: [],
  supportingDocument: [],
  transportDocument: [],
  additionalReference: [],
  additionalInformation: [],
}

function useTransitOperationForm({ isEns }: { isEns: boolean }) {
  const { validationContextRef } = useDeclarationEditorContext()
  const { customer } = useContext(UserContext)

  const { serverValidationResult } = useServerRequirementsContext('transitOperation')

  const defaultData = {
    ...initialFormData,
    customerId: customer?.id ?? initialFormData.customerId,
    declarationType: isEns ? DeclarationTypeEnum.Enum.ENS : initialFormData.declarationType,
  }

  const resolverContext: TransitOperationFormResolverContext = {
    validationContextRef,
    serverValidationResult,
  }

  const form: UseTransitOperationFormReturn = useForm<TransitOperationFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: defaultData,
    resolver: transitOperationFormResolver,
    context: resolverContext,
  })

  const populateForm = useCallback((
    {
      transitOperation,
      consignment,
      consignmentDocuments,
      guarantees,
      authorisations,
    }: Declaration,
    { keepDirtyValues = true }: { keepDirtyValues?: boolean } = {},
  ) => {
    if (transitOperation && consignment) {
      const { id: actualConsignmentId, ...rest } = consignment

      transitOperation.transitCustomsOffices.sort(sortBySequenceNumber)
      transitOperation.customsOfficesOfTransitExit.sort(sortBySequenceNumber)

      form.reset({
        ...initialFormData,
        ...transitOperation,
        ...rest,
        consignmentId: actualConsignmentId,
        consignee: consignment.consignee,
        consignor: consignment.consignor,
        carrier: consignment.carrier,
        guarantee: guarantees?.[0] ?? blankGuarantee,
        authorisations: authorisations ?? [],
        transportDocument: toTransportDocuments(consignmentDocuments),
        previousDocument: toPreviousDocuments(consignmentDocuments),
        supportingDocument: toSupportingDocuments(consignmentDocuments),
      }, {
        keepDirtyValues,
        keepErrors: true,
      })
    }
  }, [form])

  return {
    form,
    populateForm,
  }
}

export type UseTransitOperationFormReturn = UseFormReturn<TransitOperationFormValues>

export default useTransitOperationForm
